import _ from 'lodash'
import { notification } from '../../components/Notifications'
import { SurveyQuestionType } from '../../models/Survey'
import { type ISurveyItem } from '../../models/SurveyTemplate'
import { type ISurveySection } from '../../store/slices/SurveyTemplateSlice'
import { type DescriptiveQuestion, SurveyItemQuestionType, type LinearQuestion, type MultiChoiceQuestion, type Question, type FreeResponseQuestion, type ILinearObject, BranchingOperation, type ISurveyItemBranchingFilters, type IBranchingSurveyItems } from './types'

/**
 * Whether a survey item's content differs from its last-saved baseline snapshot.
 * Used when building PATCH/POST update payloads where the backend trusts `is_dirty`.
 * Items with no baseline match (e.g. just added in this session) return false.
 */
export const checkQuestionIsDirtyAgainstBaseline = (
  baselineItems: Array<ISurveyItem | Question> | undefined,
  newQuestion: Question
): boolean => {
  const baselineQuestion = baselineItems?.find((question) => question.itemId === newQuestion.itemId)
  if (!baselineQuestion) return false
  return !_.isEqual(baselineQuestion, newQuestion)
}

/**
 * Extracts a user-friendly error message from survey API error responses (e.g. 422 validation errors).
 *
 * `src/utils/api.ts` rejects with `{ message: <string>, data: <raw body> }`, where `message`
 * is derived only from top-level keys `error | message | detail | description`. A typical
 * survey 422 body is `{ surveySections: { "0": { settings: "…" } } }` (or `schema` /
 * `surveyItems`), so the actionable text lives on `payload.data`, not `payload.message`.
 * Prefer `data.surveySections`, then fall back to a nested `message.surveySections` for
 * any older callers that still pass that shape. Iterate field values rather than a single
 * key so settings-, schema-, and survey_items-keyed errors all surface.
 */
export const extractSurveyErrorMessage = (payload: any): string => {
  const surveySections =
    payload?.data?.surveySections ?? payload?.message?.surveySections
  if (surveySections && typeof surveySections === 'object') {
    for (const sectionKey of Object.keys(surveySections)) {
      const sectionErrors = surveySections[sectionKey] ?? {}
      for (const value of Object.values(sectionErrors)) {
        if (typeof value === 'string' && value.trim()) return value.trim()
        if (Array.isArray(value) && value.length > 0) return String(value[0]).trim()
      }
    }
  }
  // Fallback: if the message is a plain string, return it directly
  if (typeof payload?.message === 'string') return payload.message
  return 'Something went wrong'
}

export interface ISurveyTemplateChangeSection {
  surveySectionId: string
  name?: string
  questionCount?: number
}

export interface ISurveyTemplateUpdatedSection extends ISurveyTemplateChangeSection {
  questionCountBefore: number
  questionCountAfter: number
  settingsChanged: boolean
}

export interface ISurveyTemplateChangeSummary {
  added?: ISurveyTemplateChangeSection[]
  updated?: ISurveyTemplateUpdatedSection[]
  deleted?: ISurveyTemplateChangeSection[]
}

const SAVED_SUCCESS_FALLBACK = 'Your changes have been saved successfully.'

const changeSectionLabel = (section: { name?: string }): string =>
  section?.name?.trim() ? section.name.trim() : 'Untitled section'

/**
 * Build the success-toast subtitle summarizing which survey sections were added,
 * updated, and deleted on save, from the `changes` object returned by
 * POST /api/templates/<id>. Updated sections annotate what changed (question-count
 * delta and/or settings). Falls back to a generic saved message when the response
 * carries no change summary (older backend, or a save with no section changes).
 */
export const buildSurveyTemplateChangeSummary = (changes?: ISurveyTemplateChangeSummary): string => {
  const added = changes?.added ?? []
  const updated = changes?.updated ?? []
  const deleted = changes?.deleted ?? []

  if (added.length === 0 && updated.length === 0 && deleted.length === 0) {
    return SAVED_SUCCESS_FALLBACK
  }

  const describeUpdated = (section: ISurveyTemplateUpdatedSection): string => {
    const notes: string[] = []
    if (section.questionCountBefore !== section.questionCountAfter) {
      notes.push(`${section.questionCountBefore} → ${section.questionCountAfter} questions`)
    }
    if (section.settingsChanged) {
      notes.push('settings changed')
    }
    const label = changeSectionLabel(section)
    return notes.length > 0 ? `${label} (${notes.join(', ')})` : label
  }

  const parts: string[] = []
  if (added.length > 0) {
    parts.push(`Added ${added.length}: ${added.map(changeSectionLabel).join(', ')}`)
  }
  if (updated.length > 0) {
    parts.push(`Updated ${updated.length}: ${updated.map(describeUpdated).join(', ')}`)
  }
  if (deleted.length > 0) {
    parts.push(`Removed ${deleted.length}: ${deleted.map(changeSectionLabel).join(', ')}`)
  }

  return parts.join(' • ')
}

export const updateElementBorderColor = (id: string, color: string): void => {
  const element = document.getElementById(id)
  if (element) {
    element.style.borderColor = color
  }
}

export const scrollToElement = (id: string, color: string = 'red'): void => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    })
    updateElementBorderColor(id, color)
  }
}

export const findInvalidSection = (sections: ISurveySection[]): string | undefined => {
  if (sections) {
    for (const section of sections) {
      const { surveyItems, surveySectionId } = section
      if (!Array.isArray(surveyItems) || surveyItems.length === 0) {
        return surveySectionId
      }
      updateElementBorderColor(surveySectionId ?? '', '#D5D9DC')
    }
  }
}

const multiChoiceQuestionValidation = (question: MultiChoiceQuestion): boolean => {
  const options = question?.object || []
  const validOptions = options.every((option: string) => option !== '')
  const uniqueOptions = new Set(options).size === options.length

  if (!validOptions || question?.prompt === '' || !uniqueOptions) {
    return false
  }

  return true
}

const linearQuestionValidation = (question: LinearQuestion): boolean => {
  const options = question?.object || []

  const validOptions = options.every((option: ILinearObject) => option?.option !== '')

  const optionValues = options?.map((option: ILinearObject) => option.option)
  const uniqueOptions = new Set(optionValues)
  const allUnique = uniqueOptions.size === optionValues.length

  if (!validOptions || question?.prompt === '' || !allUnique) {
    return false
  }

  return true
}

const descriptiveQuestionValidation = (question: DescriptiveQuestion): boolean => {
  if (question.prompt === '') {
    return false
  }
  return true
}

const freeResponseQuestionValidation = (question: FreeResponseQuestion): boolean => {
  if (question?.prompt === '') {
    return false
  }
  return true
}

export const checkQuestionValidation = (question: Question): boolean => {
  switch (question?.itemType) {
    case SurveyItemQuestionType.MULTIPLE_CHOICE:
    case SurveyItemQuestionType.MULTI_SELECT:
      return multiChoiceQuestionValidation(question as MultiChoiceQuestion)
    case SurveyItemQuestionType.LINEAR_SCALE:
      return linearQuestionValidation(question as LinearQuestion)
    case SurveyItemQuestionType.FREE_RESPONSE:
      return freeResponseQuestionValidation(question as FreeResponseQuestion)
    case SurveyItemQuestionType.DESCRIPTIVE_TEXT:
      return descriptiveQuestionValidation(question as DescriptiveQuestion)
    default:
      return true
  }
}

export const findInvalidQuestion = (sections: ISurveySection[]): string | null | undefined => {
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex]
    if (!section.surveyItems) continue

    const questionIndex = section.surveyItems.find(question => !checkQuestionValidation(question))
    if (questionIndex) {
      return questionIndex.itemId
    }
  }
  return null
}

/**
 * CR-213: finds the first question that duplicates an earlier question in the SAME section
 * by (itemType, prompt) — the exact key the backend uses to dedupe survey items
 * (_reject_duplicate_items_in_section). Two same-section questions with identical prompt and
 * type collapse onto a single stored survey item, so their responses and comments duplicate
 * across both. Title (DESCRIPTIVE_TEXT) items are dividers, not questions, and are exempt to
 * match the backend. Options are intentionally NOT part of the key — the backend ignores them.
 *
 * Returns the duplicate (second) occurrence itself, or null when the sections contain no
 * same-section duplicate. Returning the question — rather than just its itemId — guarantees a
 * truthy result whenever a duplicate exists, so the save is always blocked even in the unlikely
 * case a question lacks an itemId; the caller still scrolls to it by itemId when present.
 * (Newly-added questions do get a client temp UUID at creation — see CreateFreeResponseQuestionCard
 * — so itemId is normally set; this is only a defensive backstop.)
 */
export const findDuplicateQuestionInSection = (sections: ISurveySection[]): Question | null => {
  for (const section of sections) {
    if (!section.surveyItems) continue

    const seenKeys = new Set<string>()
    for (const question of section.surveyItems) {
      if (question.itemType === SurveyItemQuestionType.DESCRIPTIVE_TEXT) continue

      const key = `${question.itemType}::${question.prompt}`
      if (seenKeys.has(key)) {
        return question
      }
      seenKeys.add(key)
    }
  }
  return null
}

//  create branch filters based on the operation
export const preparedBranchFilterForUpdateFilters = (operation: BranchingOperation, answer: any[], section: ISurveySection): IBranchingSurveyItems[] => {
  const branchFilter: IBranchingSurveyItems[] = []

  // Based on the operation, create the appropriate branch filter
  if (operation === BranchingOperation.Is) {
    answer.forEach((a: any) => {
      branchFilter.push({
        key: a,
        surveySectionName: section.name,
        surveySectionId: section.surveySectionId
      })
    })
  } else if (operation === BranchingOperation.IsNotEmpty) {
    branchFilter.push({
      key: BranchingOperation.IsNotEmpty,
      surveySectionName: section.name,
      surveySectionId: section.surveySectionId
    })
  } else if (operation === BranchingOperation.IsEmpty) {
    branchFilter.push({
      key: BranchingOperation.IsEmpty,
      surveySectionName: section.name,
      surveySectionId: section.surveySectionId
    })
  }

  return branchFilter
}

const normalizeBranchAnswers = (answers: string[] | null | undefined): string[] => {
  return [...new Set(answers ?? [])].sort((a, b) => a.localeCompare(b))
}

export const getBranchGroupKey = ({
  parentSurveySectionId,
  branchingFilters
}: {
  parentSurveySectionId?: string | null
  branchingFilters?: {
    itemId?: string
    operation?: string
    answer?: string[] | null
  } | null
}): string => {
  const normalizedAnswers = normalizeBranchAnswers(branchingFilters?.answer).join('|')
  return [
    parentSurveySectionId ?? '',
    branchingFilters?.itemId ?? '',
    branchingFilters?.operation ?? '',
    normalizedAnswers
  ].join('::')
}

export const isSectionInSameBranchGroup = (sectionA?: ISurveySection | null, sectionB?: ISurveySection | null): boolean => {
  if (!sectionA || !sectionB) return false

  return getBranchGroupKey(sectionA) === getBranchGroupKey(sectionB)
}

export const getBranchGroupSections = (sections: ISurveySection[], targetSection?: ISurveySection | null): ISurveySection[] => {
  if (!targetSection?.parentSurveySectionId || !targetSection.branchingFilters) {
    return []
  }

  const targetGroupKey = getBranchGroupKey(targetSection)
  return sections.filter((section) =>
    section.parentSurveySectionId === targetSection.parentSurveySectionId &&
    getBranchGroupKey(section) === targetGroupKey
  )
}

export const isLastSectionInBranchGroup = (sections: ISurveySection[], targetSection?: ISurveySection | null): boolean => {
  if (!targetSection?.parentSurveySectionId || !targetSection.branchingFilters) {
    return false
  }

  const matchingSections = getBranchGroupSections(sections, targetSection)
  if (!matchingSections.length) {
    return false
  }

  return matchingSections[matchingSections.length - 1].surveySectionId === targetSection.surveySectionId
}

// Check if Question option have Branch Section (checking only by single question option)
export const checkQuestionOptionHaveBranching = (index: number, question: Question | Record<string, any>): boolean => {
  const branchingKeys = question.branchingFilters
  const optionToDelete = question.itemType === SurveyQuestionType.LinearScale ? question?.object?.[index]?.option : question?.object?.[index]

  // Check if the option to delete has a branching filter associated
  const isOptionBranching = branchingKeys?.find((option: IBranchingSurveyItems) => option.key === optionToDelete)

  if (isOptionBranching) {
    const sectionName = question.branchingFilters?.[optionToDelete]?.name
    notification(
      'This option cannot be deleted because it contains a branch section',
      {
        variant: 'error',
        subTitle: `Linked Section: ${isOptionBranching.name || 'Unknown Section'}`
      }
    )
    return true
  }

  return false
}

// Get Child Branches section ids  By parent question
export const getUniqueChildBranchesDetails = (itemBranchFilters: ISurveyItemBranchingFilters): IBranchingSurveyItems[] | undefined => {
  const uniqueBranchingFilters = itemBranchFilters?.filter((value, index, self) =>
    index === self?.findIndex((t) => (
      t.surveySectionId === value.surveySectionId
    ))
  )
  return uniqueBranchingFilters
}

// Helper function to update branching filters in a parent section
export const updateBranchingFiltersSectionNameInParentSection = (
  parentSurveySection?: { surveyItems?: any[] },
  surveySectionId?: any,
  newName?: any
): void => {
  parentSurveySection?.surveyItems?.forEach((surveyItem) => {
    if (surveyItem.branchingFilters) {
      surveyItem.branchingFilters.forEach((branchingFilter: IBranchingSurveyItems) => {
        if (branchingFilter.surveySectionId === surveySectionId) {
          branchingFilter.surveySectionName = newName
        }
      })
    }
  })
}

/**
 * Finds the parent section and corresponding parent question for a given survey section,
 * based on its parentSurveySectionId and branchingFilters' itemId.
**/
export const findParentSectionAndQuestion = (sectionsList: ISurveySection[], section: ISurveySection): { parentSection: ISurveySection, parentQuestion: Question } | undefined => {
  const parentSection = sectionsList?.find((s: ISurveySection) => s.surveySectionId === section.parentSurveySectionId)
  const parentQuestion = parentSection?.surveyItems?.find((q: Question) => q.itemId === section?.branchingFilters?.itemId)

  if (parentSection && parentQuestion) {
    return { parentSection, parentQuestion }
  }

  return undefined
}

// Determines if an operation option should be disabled based on existing branching filters or if the operation is 'IsEmpty' and the question is required.
export const isOperationOptionDisabled = (operation: { title: string, value: BranchingOperation }, questionData: Question | undefined): boolean | undefined => {
  return operation.value === BranchingOperation.IsEmpty && questionData?.isRequired
}

export const checkQuestionOptionHaveBranch = (option: any, question?: Question): boolean => {
  // option.option is useing for linear question  {option:somevalue,weight}
  const isExist = !!question?.branchingFilters?.find(b => option.option ? option.option === b.key : option === b.key)
  return isExist
}

// Only Disabled that option which have linked to other branch section user can remove the currently linked option and more which have not linked to any branch section
export const checkQuestionOptionHaveBranchForFilterModal = (option: any, question: Question | undefined, section: ISurveySection | undefined): boolean => {
  const branchingKeys = question?.branchingFilters?.map(b => b.key)
  const optionValue = option?.option ?? option
  const x = branchingKeys?.includes(optionValue) && !section?.branchingFilters?.answer.includes(optionValue)
  return x ?? false
}
